import posthog from "posthog-js"

import type { CurrentAccount, CurrentUser } from "@/types"

// ─── Event name union ──────────────────────────────────────────────
export type AnalyticsEvent =
  // Auth
  | "user_signed_up"
  | "user_signed_in"
  // Onboarding
  | "onboarding_step_completed"
  | "onboarding_completed"
  | "onboarding_listing_imported"
  | "onboarding_listing_skipped"
  // Listings
  | "listing_imported"
  | "listing_created"
  | "listing_updated"
  | "listing_deleted"
  // Videos — wizard
  | "video_created"
  | "video_wizard_step_advanced"
  | "video_script_generated"
  | "video_voiceover_generated"
  | "video_music_generated"
  | "video_scene_enhanced"
  | "video_scene_animated"
  | "video_render_started"
  | "video_render_completed"
  | "video_deleted"
  | "video_renamed"
  | "video_duplicated"
  // Videos — render step
  | "video_downloaded"
  | "video_share_link_copied"
  // Publishing
  | "caption_generated"
  | "social_post_published"
  | "social_post_cancelled"
  | "social_post_retried"
  | "listing_post_published"
  | "listing_caption_generated"
  | "standalone_post_published"
  | "standalone_caption_generated"
  | "standalone_video_post_published"
  | "standalone_video_caption_generated"
  // Social connections
  | "social_connection_initiated"
  | "social_connection_disconnected"
  | "auto_publish_rule_updated"
  // Credits
  | "credits_purchase_clicked"
  | "credits_plan_purchased"
  | "credits_portal_opened"
  // Intro offer (new-agent welcome)
  | "intro_offer_shown"
  | "intro_offer_claimed"
  | "intro_offer_dismissed"
  // Team management
  | "member_invited"
  | "member_role_changed"
  | "member_removed"
  | "invitation_revoked"
  // Settings & profile
  | "account_settings_saved"
  | "logo_uploaded"
  | "logo_removed"
  | "profile_updated"
  | "avatar_uploaded"
  // Public lead forms
  | "demo_request_submitted"
  | "get_started_submitted"
  | "canada_waitlist_submitted"
  // Landing pages — boutique-secret-weapon and any future /lp/* page
  | "lp_view"
  | "lp_view_15s"
  | "cta_click"
  | "lead_submitted"

// ─── Google Analytics helper ──────────────────────────────────────
function gtagEvent(
  event: string,
  params?: Record<string, unknown>,
) {
  if (typeof window.gtag === "function") {
    window.gtag("event", event, params)
  }
}

// ─── Meta Pixel helper ────────────────────────────────────────────
//
// Maps the QuiteRight internal event name to a Meta Pixel standard event
// where one applies. Standard events feed Meta's optimisation algorithms
// directly — they're the events you can pick from the dropdown when
// configuring conversion campaigns. Anything not listed here is still
// captured by PostHog + GA4, just not pushed to Meta.
//
// Reference: https://developers.facebook.com/docs/meta-pixel/reference
const META_STANDARD_EVENTS: Partial<Record<AnalyticsEvent, string>> = {
  // Landing page funnel
  lp_view: "ViewContent",
  cta_click: "Lead",
  // Lead form submissions across LP campaigns map to Meta's Lead standard event
  // (the canada-first-50 LP sets this as the optimisation target)
  lead_submitted: "Lead",
  canada_waitlist_submitted: "Lead",
  // Sign-up funnel — fires on /sign-up form submit (email or Google)
  user_signed_up: "CompleteRegistration",
  // Subscription / paid conversion
  credits_plan_purchased: "Purchase",
}

function metaEvent(
  event: AnalyticsEvent,
  properties?: Record<string, unknown>,
) {
  const standard = META_STANDARD_EVENTS[event]
  if (!standard || typeof window.fbq !== "function") return
  // Prefer trackCustom if the mapped name is non-standard, but every value
  // in META_STANDARD_EVENTS is a Meta-recognised standard event so plain
  // track is correct here.
  window.fbq("track", standard, properties ?? {})
}

// ─── Typed track wrapper ───────────────────────────────────────────
export function track(
  event: AnalyticsEvent,
  properties?: Record<string, unknown>,
) {
  posthog.capture(event, properties)
  gtagEvent(event, properties)
  metaEvent(event, properties)
}

// ─── Page view tracking (SPA navigations) ─────────────────────────
export function trackPageView(url: string) {
  gtagEvent("page_view", {
    page_location: url,
  })
}

// ─── User identification ──────────────────────────────────────────
export function identifyUser(user: CurrentUser, account: CurrentAccount) {
  posthog.identify(String(user.id), {
    email: user.email,
    name: user.name,
    is_superadmin: user.is_superadmin,
  })

  posthog.group("account", String(account.id), {
    name: account.name,
    account_type: account.account_type,
  })

  gtagEvent("login", { method: "session" })
  if (typeof window.gtag === "function") {
    window.gtag("set", "user_properties", {
      user_id: String(user.id),
      account_id: String(account.id),
      account_type: account.account_type,
    })
  }
}

export function resetUser() {
  posthog.reset()
}
